'use client';

import Link from 'next/link';
import { useEffect } from 'react';

import { PMNeuralMesh, PMWordmark } from '@/features/marketing';

/**
 * Brand-quality global error page.
 *
 * Next.js renders this when a React render throws inside any route in the
 * tree (it does NOT cover API failures — those flow through baseQuery and
 * surface as `ApiUnreachablePage` or per-feature widget boundaries).
 *
 * The page is intentionally non-technical. We never print stack traces or
 * raw error messages to the user — those would scare without informing.
 * Instead we show the support email + the error digest, which is what the
 * support team needs to find the trace in Sentry.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Mirror to the console for devs. Sentry's automatic Next.js
    // integration is already capturing this through the error.digest
    // pipeline, so we don't double-report here.
    console.error('Unhandled render error:', error);
  }, [error]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--pm-cream)',
        color: 'var(--pm-ink)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -80,
          right: -120,
          opacity: 0.4,
          pointerEvents: 'none',
        }}
      >
        <PMNeuralMesh
          width={720}
          height={520}
          color="var(--pm-coral)"
          density={1.1}
          opacity={0.4}
        />
      </div>

      <header
        style={{
          padding: '28px 40px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Link href="/" aria-label="Back to PropMind home">
          <PMWordmark size={22} color="var(--pm-ink)" accent="var(--pm-coral)" />
        </Link>
      </header>

      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px 80px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div style={{ maxWidth: 620, width: '100%' }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: 'var(--pm-coral-soft)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 22,
            }}
            aria-hidden
          >
            <svg
              width={26}
              height={26}
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--pm-coral-deep)"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.41 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          <div
            style={{
              fontSize: 11,
              letterSpacing: 1.6,
              textTransform: 'uppercase',
              fontWeight: 700,
              color: 'var(--pm-coral)',
              marginBottom: 14,
            }}
          >
            Something went wrong
          </div>

          <h1
            style={{
              fontFamily: 'var(--pm-serif)',
              fontWeight: 400,
              fontSize: 'clamp(2rem, 5vw, 3.25rem)',
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              margin: '0 0 20px',
              color: 'var(--pm-ink)',
            }}
          >
            We hit a snag rendering this page.
          </h1>

          <p
            style={{
              fontSize: 16,
              lineHeight: 1.6,
              color: 'var(--pm-ink-3)',
              margin: '0 0 30px',
            }}
          >
            Your account, your rules, your journal, and your connected brokers
            are all safe — this was a problem on the page itself, not your
            data. Try again, and if it keeps happening let us know.
          </p>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 12,
              marginBottom: 36,
            }}
          >
            <button
              type="button"
              onClick={reset}
              style={{
                padding: '11px 20px',
                borderRadius: 11,
                background: 'var(--pm-ink-fixed)',
                color: 'var(--pm-on-ink)',
                border: 'none',
                fontSize: 14.5,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <Link
              href="/"
              style={{
                padding: '11px 20px',
                borderRadius: 11,
                border: '1px solid var(--pm-rule-strong)',
                background: 'var(--pm-paper)',
                color: 'var(--pm-ink)',
                fontSize: 14.5,
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              Back to home
            </Link>
            <Link
              href="/dashboard"
              style={{
                fontSize: 13.5,
                color: 'var(--pm-ink-3)',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              Open my dashboard →
            </Link>
          </div>

          <div
            style={{
              padding: '14px 16px',
              borderRadius: 10,
              border: '1px solid var(--pm-rule)',
              background: 'var(--pm-paper)',
              fontSize: 12.5,
              lineHeight: 1.6,
              color: 'var(--pm-ink-3)',
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                fontWeight: 700,
                color: 'var(--pm-ink-4)',
                marginBottom: 6,
              }}
            >
              Reporting this
            </div>
            Email{' '}
            <a
              href="mailto:support@propmind.app"
              style={{
                color: 'var(--pm-ink-2)',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              support@propmind.app
            </a>{' '}
            with what you were doing.
            {error.digest && (
              <>
                {' '}
                Include this reference so we can pull the trace:{' '}
                <code
                  style={{
                    fontFamily: 'var(--pm-mono)',
                    fontSize: 12,
                    background: 'var(--pm-cream-2)',
                    padding: '1px 6px',
                    borderRadius: 4,
                    color: 'var(--pm-ink-2)',
                    wordBreak: 'break-all',
                  }}
                >
                  {error.digest}
                </code>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
